import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Form } from '@amzn/awsui-community-rjsf';
import validator from 'src/components/datausageagreement/cfWorkerFormValidator';
import { RegistryWidgetsType, RJSFSchema, WidgetProps } from '@rjsf/utils';
import { CheckboxOnlyAfterClickLinkWidget } from 'src/components/datausageagreement/widgets/CheckboxOnlyAfterClickLinkWidget';
import getDefaultFormState from '@rjsf/utils/lib/schema/getDefaultFormState';
import DOMPurify from 'dompurify';
import { ConditionallyDisabledInput } from 'src/components/datausageagreement/widgets/ConditionallyDisabledInput';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';

export interface UsageAgreementFormProps {
  formData: object;
  setFormData: any;
  errors: any[];
  setErrors: any;
  schema: string;
  readOnly: boolean;
  omniContext?: OmniDUAContext;
}

const sanitizeSchema = (schema: RJSFSchema): RJSFSchema => {
  const sanitizeValue = (value: any): any => {
    if (typeof value === 'string') {
      return DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    }
    if (Array.isArray(value)) {
      return value.map(sanitizeValue);
    }
    if (value && typeof value === 'object') {
      const sanitized: any = {};
      for (const [key, val] of Object.entries(value)) {
        sanitized[key] = sanitizeValue(val);
      }
      return sanitized;
    }
    return value;
  };
  return sanitizeValue(schema);
};

const validateSchemaStructure = (schema: any): boolean => {
  if (!schema || typeof schema !== 'object') return false;
  if (schema.type && !['object', 'string', 'number', 'boolean', 'array'].includes(schema.type)) return false;
  return true;
};

const sanitizeFormData = (data: any): any => {
  if (typeof data === 'string') {
    return DOMPurify.sanitize(data, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }
  if (Array.isArray(data)) {
    return data.map(sanitizeFormData);
  }
  if (data && typeof data === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[key] = sanitizeFormData(value);
    }
    return sanitized;
  }
  return data;
};

export const UsageAgreementForm = (props: UsageAgreementFormProps) => {
  useEffect(() => {}, []);

  const [propertyOrder, setPropertyOrder] = useState<string[]>([]);

  const rjsfSchema = useMemo(() => {
    try {
      const rawSchema = JSON.parse(props.schema) as object;
      const parsedSchema = rawSchema as RJSFSchema;
      if (!!rawSchema?.['order']) {
        setPropertyOrder(rawSchema['order']);
      }
      if (!validateSchemaStructure(parsedSchema)) {
        throw new Error('Invalid schema structure');
      }
      return sanitizeSchema(parsedSchema);
    } catch (error) {
      console.error('Invalid schema provided:', error);
      return { type: 'object', properties: {} } as RJSFSchema;
    }
  }, [props.schema]);

  const sanitizedFormData = useMemo(() => sanitizeFormData(props.formData), [props.formData]);

  if (JSON.stringify(props.formData) === '{}') {
    props.setFormData(getDefaultFormState(validator, rjsfSchema, props.formData, rjsfSchema, true) as object);
  }

  const widgets: RegistryWidgetsType = useMemo(
    () => ({
      CheckboxWidget: (props: WidgetProps) => <CheckboxOnlyAfterClickLinkWidget {...props} />,
      TextWidget: (widgetProps: WidgetProps) => (
        <ConditionallyDisabledInput {...widgetProps} omniContext={props.omniContext} />
      ),
    }),
    [props.omniContext],
  );

  const getRootLevelErrorText = () => {
    const rootLevelErrors = props.errors.filter((error) => error.schemaPath == '#');
    if (rootLevelErrors.length == 0) {
      return;
    }
    return rootLevelErrors[0].message;
  };

  let uiSchema = {
    'ui:widget': 'checkbox',
    'ui:submitButtonOptions': {
      norender: true,
    },
  };
  if (propertyOrder?.length > 0) {
    uiSchema['ui:order'] = propertyOrder;
  }

  return (
    <Form
      readonly={props.readOnly}
      validator={validator}
      liveValidate={true}
      schema={rjsfSchema}
      formData={sanitizedFormData}
      onChange={(data) => {
        const sanitizedData = sanitizeFormData(data.formData);
        props.setFormData(sanitizedData);
        props.setErrors(data.errors);
      }}
      transformErrors={() => {
        return props.errors;
      }}
      extraErrorsBlockSubmit={true}
      showErrorList={'bottom'}
      errorText={getRootLevelErrorText()}
      uiSchema={uiSchema}
      noHtml5Validate={true}
      widgets={widgets}
      omitExtraData={true}
      liveOmit={true}
    />
  );
};
